/**
 * Backend API Service for the Employee Engagement Survey.
 *
 * POST (employee submit) is gated by a shared password sent as
 * `X-Survey-Password`. Budtenders don't have @sweetspotfarms.com Google
 * accounts, so the submission path uses a shared password distributed
 * by managers rather than Firebase auth. The payload still carries no
 * identifier — submissions stay anonymous.
 *
 * GET (admin list) requires a Firebase ID token; the backend's
 * `verifyFirebaseToken` + `requireSurveyResultsAccess` middlewares enforce
 * the @sweetspotfarms.com domain restriction plus an explicit email
 * allowlist for the three managers who view results.
 */

import { SurveyTrack } from '../components/survey/surveyQuestions.ts';
import { getSurveyPassword } from '../components/survey/SurveyPasswordGate.tsx';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL!;

export interface SurveyResponsePayload {
  track: SurveyTrack;
  location: string;
  department?: string;
  answers: Record<string, string | number>;
  comments: Record<string, string>;
}

export interface SurveyResponseRecord extends SurveyResponsePayload {
  id: string;
  submittedAt: string;
}

const getAuthToken = async (): Promise<string> => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('No authenticated user found');
  return auth.currentUser.getIdToken();
};

export const submitSurveyResponse = async (payload: SurveyResponsePayload): Promise<void> => {
  const password = getSurveyPassword();
  if (!password) throw new Error('No survey password — please re-enter.');

  const response = await fetch(`${API_BASE_URL}/api/survey-responses`, {
    method: 'POST',
    headers: {
      'X-Survey-Password': password,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const j = await response.json();
      if (j?.error) detail = j.error;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
};

export const listSurveyResponses = async (filters?: {
  track?: SurveyTrack;
  location?: string;
}): Promise<SurveyResponseRecord[]> => {
  const token = await getAuthToken();
  const qs = new URLSearchParams();
  if (filters?.track) qs.set('track', filters.track);
  if (filters?.location) qs.set('location', filters.location);
  const url = `${API_BASE_URL}/api/survey-responses${qs.toString() ? `?${qs}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const errBody = await response.json();
      if (errBody?.error) detail = errBody.error;
    } catch {
      // Non-JSON error body (auth proxy HTML, gateway 502, etc.) — keep the status-only detail.
    }
    throw new Error(detail);
  }

  const result = await response.json();
  return Array.isArray(result?.data) ? (result.data as SurveyResponseRecord[]) : [];
};
